import { useMemo, useCallback } from 'react';
import { useSubscription, useCreatePortalSession } from './api/useSubscription';
import { useOrganization } from './useOrganization';

export interface SubscriptionLimitInfo {
  // Status
  isLoading: boolean;
  isTrialMode: boolean;
  isTrialExpired: boolean;
  isSubscriptionActive: boolean;
  hasValidSubscription: boolean;

  // Limits
  assetLimit: number | null;
  currentAssetCount: number;
  assetsRemaining: number | null;
  isAtAssetLimit: boolean;
  isOverAssetLimit: boolean;

  // Dates
  trialExpiresAt: Date | null;
  subscriptionEndsAt: Date | null;
  daysUntilExpiry: number | null;

  // Actions
  goToBilling: () => void;
  canCreateAsset: boolean;
  limitMessage: string | null;
}

export function useSubscriptionLimits(): SubscriptionLimitInfo {
  const { organizationId } = useOrganization();
  const { data: subscription, isLoading } = useSubscription(organizationId);
  const createPortalSession = useCreatePortalSession();

  // Create goToBilling outside useMemo to avoid dependency issues
  const goToBilling = useCallback(() => {
    if (organizationId) {
      createPortalSession.mutate(organizationId);
    }
  }, [organizationId, createPortalSession]);

  const result = useMemo((): SubscriptionLimitInfo => {
    const now = new Date();

    // Default values when loading or no subscription data
    if (!subscription) {
      return {
        isLoading,
        isTrialMode: false,
        isTrialExpired: false,
        isSubscriptionActive: false,
        hasValidSubscription: false,
        assetLimit: null,
        currentAssetCount: 0,
        assetsRemaining: null,
        isAtAssetLimit: false,
        isOverAssetLimit: false,
        trialExpiresAt: null,
        subscriptionEndsAt: null,
        daysUntilExpiry: null,
        goToBilling,
        canCreateAsset: true, // Allow by default when loading
        limitMessage: null,
      };
    }

    // Parse dates
    const trialExpiresAt = subscription.trial_expires_at
      ? new Date(subscription.trial_expires_at)
      : null;
    const subscriptionEndsAt = subscription.subscription_period_end
      ? new Date(subscription.subscription_period_end)
      : null;

    // Check trial status
    const isTrialMode = subscription.trial_mode ?? false;
    const isTrialExpired = !!(isTrialMode && trialExpiresAt && trialExpiresAt < now);

    // Check subscription status
    const isSubscriptionActive = ['active', 'trialing'].includes(
      subscription.subscription_status || ''
    );
    const isCanceledButStillActive = !!(
      subscription.subscription_cancel_at_period_end &&
      subscriptionEndsAt &&
      subscriptionEndsAt > now
    );

    const hasValidSubscription = isSubscriptionActive || isCanceledButStillActive;

    // Check asset limits
    const assetLimit = subscription.asset_limit;
    const currentAssetCount = subscription.current_asset_count || 0;
    const assetsRemaining = assetLimit !== null ? Math.max(0, assetLimit - currentAssetCount) : null;
    const isAtAssetLimit = assetLimit !== null && currentAssetCount >= assetLimit;
    const isOverAssetLimit = assetLimit !== null && currentAssetCount > assetLimit;

    // Calculate days until expiry
    let daysUntilExpiry: number | null = null;
    const expiryDate = isTrialMode ? trialExpiresAt : subscriptionEndsAt;
    if (expiryDate && expiryDate > now) {
      daysUntilExpiry = Math.ceil(
        (expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
      );
    }

    // Determine if user can create asset
    const canCreateAsset = !isTrialExpired && !isAtAssetLimit;

    // Generate limit message
    let limitMessage: string | null = null;
    if (isTrialExpired) {
      limitMessage = 'Your trial has expired. Please subscribe to continue using TrustPuma.';
    } else if (isOverAssetLimit) {
      limitMessage = `You have exceeded your asset limit (${assetLimit}). Please upgrade your plan to add more assets.`;
    } else if (isAtAssetLimit) {
      limitMessage = `You have reached your asset limit (${assetLimit}). Upgrade your plan to add more assets.`;
    } else if (isTrialMode && daysUntilExpiry !== null && daysUntilExpiry <= 7) {
      limitMessage = `Your trial expires in ${daysUntilExpiry} day${daysUntilExpiry === 1 ? '' : 's'}. Subscribe now to keep access.`;
    }

    return {
      isLoading,
      isTrialMode,
      isTrialExpired,
      isSubscriptionActive,
      hasValidSubscription,
      assetLimit,
      currentAssetCount,
      assetsRemaining,
      isAtAssetLimit,
      isOverAssetLimit,
      trialExpiresAt,
      subscriptionEndsAt,
      daysUntilExpiry,
      goToBilling,
      canCreateAsset,
      limitMessage,
    };
  }, [subscription, isLoading, goToBilling]);

  return result;
}
